import {
  Checkbox,
  Flex,
  FormControl,
  Box,
  Text,
  Tooltip,
  Icon,
  HStack,
  Button,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Switch,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Badge,
  Select,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import {
  MdPublic,
  MdLock,
  MdOutlineExplore,
  MdEvStation,
} from "react-icons/md";
import { FaTowerBroadcast } from "react-icons/fa6";
import useAxios from "axios-hooks";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Card from "components/card/Card";

// Glass morphism styled components
const GlassMorphismCard = styled(Card)`
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(245, 125, 54, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    border-color: rgba(245, 125, 54, 0.4);
    box-shadow: 0 8px 32px rgba(245, 125, 54, 0.15);
  }
`;

const FuturisticButton = styled(Button)`
  background: linear-gradient(135deg, #f57d36 0%, #f35626 100%);
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(245, 125, 54, 0.3);
  color: white;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  padding: 0 24px;
  height: 40px;

  &:hover {
    background: linear-gradient(135deg, #f35626 0%, #f57d36 100%);
    box-shadow: 0 6px 16px rgba(245, 125, 54, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(245, 125, 54, 0.3);
  }
`;

const Roaming = ({ id }) => {
  const toast = useToast();
  const [locationData, setLocationData] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const cancelRef = React.useRef();

  // Stations belonging to this location, and their per-station OCPI enablement
  const [stations, setStations] = useState([]);
  const [stationOcpiFilter, setStationOcpiFilter] = useState("all");
  const filteredStations = useMemo(() => {
    if (stationOcpiFilter === "enabled") {
      return stations.filter((station) => station.IsOcpiEnabled);
    }
    if (stationOcpiFilter === "disabled") {
      return stations.filter((station) => !station.IsOcpiEnabled);
    }
    return stations;
  }, [stations, stationOcpiFilter]);
  const [stationConfirm, setStationConfirm] = useState({
    open: false,
    station: null,
    nextValue: false,
  });
  const [updatingStationId, setUpdatingStationId] = useState(null);
  const stationCancelRef = React.useRef();

  // Fetch location data
  const [{ data, loading: fetchLoading, error: fetchError }, fetchLocation] =
    useAxios(
      {
        url: `/api/locations/${id}`,
        method: "GET",
      },
      { manual: true }
    );

  // Fetch stations for this location
  const [{ data: stationsData, loading: stationsLoading }, fetchStations] =
    useAxios(
      {
        url: `/api/stations/forLocation/${id}`,
        method: "GET",
      },
      { manual: true }
    );

  // Toggle a single station's OCPI enablement
  const [, executeStationUpdate] = useAxios({}, { manual: true });

  // Update location settings
  const [{ loading: updateLoading, error: updateError }, executeUpdate] =
    useAxios(
      {
        method: "PATCH",
        url: `/api/locations/update/${id}`,
      },
      { manual: true }
    );

  // Broadcast location to OCPI
  const [
    { loading: broadcastLoading, error: broadcastError },
    executeBroadcast,
  ] = useAxios(
    {
      method: "POST",
      url: `/api/locations/broadcast/${id}`,
    },
    { manual: true }
  );

  // Schema for validation
  const updateSchema = yup.object().shape({
    Private: yup.boolean(),
    Roaming: yup.boolean(),
  });

  // Form validation
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(updateSchema),
  });

  // Fetch location data on component mount
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      if (!id) return;

      try {
        await fetchLocation({
          signal: controller.signal,
        });
        await fetchStations({
          signal: controller.signal,
        });
      } catch (error) {
        // Only log errors if the component is still mounted and it's not an abort error
        if (isMounted && error.code !== "ERR_CANCELED") {
          console.error("Error fetching location data:", error);
        }
      }
    };

    fetchData();

    // Cleanup function to cancel pending requests when unmounting
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id, fetchLocation, fetchStations]);

  // Update form values when data is fetched
  useEffect(() => {
    if (data?.data) {
      setLocationData(data.data);

      // Set form values
      if (data.data.Publish !== undefined) {
        setValue("Private", !data.data.Publish);
      }

      if (data.data.IsOcpiEnabled !== undefined) {
        setValue("Roaming", data.data.IsOcpiEnabled);
      }
    }
  }, [data, setValue]);

  // Update stations list when fetched
  useEffect(() => {
    if (stationsData?.data) {
      setStations(stationsData.data);
    }
  }, [stationsData]);

  // Open confirmation dialog
  const openConfirmDialog = () => {
    setIsConfirmOpen(true);
  };

  // Close confirmation dialog
  const closeConfirmDialog = () => {
    setIsConfirmOpen(false);
  };

  // Toggling a station's OCPI switch: ask for confirmation before disabling,
  // apply immediately when enabling. Stations can only be enabled for OCPI
  // when their location is already OCPI-enabled.
  const handleStationToggle = (station, nextValue) => {
    if (!locationData?.IsOcpiEnabled) {
      toast({
        title: "Enable roaming for this location first",
        description:
          "Turn on OCPI roaming for the location above before enabling individual stations.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (station.IsOcpiEnabled && !nextValue) {
      setStationConfirm({ open: true, station, nextValue });
      return;
    }
    applyStationOcpiUpdate(station, nextValue);
  };

  const closeStationConfirm = () => {
    setStationConfirm({ open: false, station: null, nextValue: false });
  };

  const applyStationOcpiUpdate = async (station, nextValue) => {
    setUpdatingStationId(station.Id);
    try {
      await executeStationUpdate({
        method: "PATCH",
        url: `/api/stations/update/${station.Id}`,
        data: { IsOcpiEnabled: nextValue },
      });
      toast({
        title: `OCPI ${nextValue ? "enabled" : "disabled"} for station ${
          station.StationId || station.Id
        }`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      fetchStations();
    } catch (error) {
      const err = error?.response?.data?.error;
      toast({
        title: "Error updating station OCPI setting",
        description: err || "Please try again later",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingStationId(null);
    }
  };

  const executeStationConfirmHandler = async () => {
    const { station, nextValue } = stationConfirm;
    closeStationConfirm();
    if (station) {
      await applyStationOcpiUpdate(station, nextValue);
    }
  };

  // Handler to broadcast location to OCPI after confirmation
  const executeBroadcastHandler = async () => {
    closeConfirmDialog();

    const controller = new AbortController();
    try {
      await executeBroadcast({
        signal: controller.signal,
      });
      toast({
        title: "Location broadcasted successfully to OCPI",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Refresh location data
      fetchLocation({
        signal: controller.signal,
      });
    } catch (error) {
      // Don't show errors for canceled requests
      if (error.name === "CanceledError") return;

      const err = error?.response?.data?.error;
      toast({
        title: "Error broadcasting location to OCPI",
        description: err || "Please try again later",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const updateHandler = async () => {
    const formValues = getValues();
    let updateData = {};

    // Check if Private (Publish) status has changed
    if (
      formValues.Private !== undefined &&
      !formValues.Private !== locationData?.Publish
    ) {
      updateData["Publish"] = !formValues.Private;
    }

    // Check if Roaming (IsOcpiEnabled) status has changed
    if (
      formValues.Roaming !== undefined &&
      formValues.Roaming !== locationData?.IsOcpiEnabled
    ) {
      updateData["IsOcpiEnabled"] = formValues.Roaming;
    }

    if (Object.keys(updateData).length > 0) {
      const controller = new AbortController();
      try {
        await executeUpdate({
          data: updateData,
          signal: controller.signal,
        });
        toast({
          title: "Location Updated successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Use the same abort controller for the refresh request
        fetchLocation({
          signal: controller.signal,
        });
      } catch (error) {
        // Don't show errors for canceled requests
        if (error.name === "CanceledError") return;

        const err = error?.response?.data?.error;
        toast({
          title: "Error occurred when updating location",
          description: err || "Please try again later",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      toast({
        title: "Nothing new to update",
        status: "warning",
        position: "top",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (fetchLoading && !locationData) {
    return (
      <Box p={5} textAlign="center">
        Loading location data...
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box p={5} textAlign="center" color="red.500">
        Error loading location data. Please try refreshing the page.
      </Box>
    );
  }

  if (!locationData) {
    return (
      <Box p={5} textAlign="center">
        Location data not found.
      </Box>
    );
  }

  return (
    <>
    <GlassMorphismCard>
      <Flex mb={4} alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          <Icon as={MdPublic} boxSize={6} color="#f57d36" mr={2} />
          <Text fontSize="xl" fontWeight="600">
            Roaming Configuration
          </Text>
        </Flex>

        <Button
          onClick={openConfirmDialog}
          leftIcon={<FaTowerBroadcast />}
          bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
          color="white"
          borderRadius="8px"
          boxShadow="0 4px 12px rgba(245, 125, 54, 0.3)"
          fontWeight="600"
          transition="all 0.2s ease"
          size="md"
          _hover={{
            bgGradient: "linear(135deg, #f35626 0%, #f57d36 100%)",
            boxShadow: "0 6px 16px rgba(245, 125, 54, 0.4)",
            transform: "translateY(-2px)",
          }}
          _active={{
            transform: "translateY(0)",
            boxShadow: "0 2px 8px rgba(245, 125, 54, 0.3)",
          }}
          isLoading={broadcastLoading}
          loadingText="Broadcasting..."
        >
          Broadcast Now
        </Button>
      </Flex>

      <Box mb={5}>
        <Text fontSize="md" mb={3}>
          Configure the visibility and roaming settings for this charging
          location.
        </Text>
      </Box>

      <form onSubmit={handleSubmit(updateHandler)}>
        <Box
          p={5}
          bg="rgba(255, 255, 255, 0.4)"
          borderRadius="12px"
          border="1px solid rgba(245, 125, 54, 0.15)"
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
          mb={5}
        >
          <Text mb={4} fontSize="lg" fontWeight="500" color="#333">
            Visibility & Access Settings
          </Text>
          <Flex direction={{ base: "column", md: "row" }} gap={8} mb={4}>
            <Controller
              name="Private"
              control={control}
              defaultValue={locationData?.Publish ? false : true}
              render={({ field }) => (
                <FormControl display="flex" alignItems="center">
                  <Checkbox
                    {...field}
                    colorScheme="orange"
                    size="lg"
                    isChecked={field.value}
                    borderColor="rgba(245, 125, 54, 0.5)"
                    sx={{
                      "& .chakra-checkbox__control": {
                        borderRadius: "6px",
                        _checked: {
                          bg: "#f57d36",
                          borderColor: "#f57d36",
                        },
                      },
                    }}
                  >
                    <HStack spacing={2} align="center">
                      <Icon as={MdLock} color="#f57d36" boxSize={5} />
                      <Box>
                        <Text fontWeight="500">Private Location</Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          When enabled, this location will not be publicly
                          visible
                        </Text>
                      </Box>
                    </HStack>
                  </Checkbox>
                  <Tooltip
                    label="When enabled, this location will not be publicly visible in applications or maps"
                    hasArrow
                    placement="top"
                  >
                    <Icon as={MdOutlineExplore} ml={2} color="gray.400" />
                  </Tooltip>
                </FormControl>
              )}
            />

            <Controller
              name="Roaming"
              control={control}
              defaultValue={locationData?.IsOcpiEnabled}
              render={({ field }) => (
                <FormControl display="flex" alignItems="center">
                  <Checkbox
                    {...field}
                    colorScheme="orange"
                    size="lg"
                    isChecked={field.value}
                    borderColor="rgba(245, 125, 54, 0.5)"
                    sx={{
                      "& .chakra-checkbox__control": {
                        borderRadius: "6px",
                        _checked: {
                          bg: "#f57d36",
                          borderColor: "#f57d36",
                        },
                      },
                    }}
                  >
                    <HStack spacing={2} align="center">
                      <Icon as={MdPublic} color="#f57d36" boxSize={5} />
                      <Box>
                        <Text fontWeight="500">Enable Roaming</Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Enable OCPI roaming for this location
                        </Text>
                      </Box>
                    </HStack>
                  </Checkbox>
                  <Tooltip
                    label="Enable OCPI roaming for this location to connect with other charging networks"
                    hasArrow
                    placement="top"
                  >
                    <Icon as={MdOutlineExplore} ml={2} color="gray.400" />
                  </Tooltip>
                </FormControl>
              )}
            />
          </Flex>

          {locationData?.IsOcpiEnabled && (
            <Box
              mt={4}
              p={4}
              bg="rgba(245, 125, 54, 0.08)"
              borderRadius="8px"
              border="1px dashed rgba(245, 125, 54, 0.3)"
            >
              <Flex align="center" mb={2}>
                <Icon as={MdEvStation} color="#f57d36" mr={2} />
                <Text fontWeight="500">Roaming Status</Text>
              </Flex>
              <Text fontSize="sm">
                This location is currently enabled for roaming with OCPI Party
                ID: <strong>{locationData?.PartyId || "N/A"}</strong>
              </Text>
            </Box>
          )}
        </Box>

        <Flex justify="center" flexWrap="wrap" gap={4}>
          <Tooltip label="Save roaming configuration" hasArrow placement="top">
            <FuturisticButton
              type="submit"
              isLoading={updateLoading}
              leftIcon={<Icon as={MdPublic} />}
            >
              Save Configuration
            </FuturisticButton>
          </Tooltip>
        </Flex>
      </form>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeConfirmDialog}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay bg="rgba(0, 0, 0, 0.2)" backdropFilter="blur(12px)">
          <AlertDialogContent
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="16px"
            border="1px solid rgba(245, 125, 54, 0.4)"
            boxShadow="0 8px 32px rgba(245, 125, 54, 0.15)"
            p={3}
            overflow="hidden"
            position="relative"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              // bgGradient: 'linear(to-r, #f57d36, #f35626)',
            }}
            transition="all 0.3s ease"
          >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="600"
              color="#333"
              pb={2}
              borderBottom="1px solid rgba(245, 125, 54, 0.2)"
            >
              <Flex align="center">
                <Icon as={FaTowerBroadcast} mr={2} color="#f57d36" boxSize={5} />
                Confirm OCPI Broadcast
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              {locationData?.IsOcpiEnabled ? (
                <Box
                  p={3}
                  bg="rgba(245, 125, 54, 0.05)"
                  borderRadius="12px"
                  mb={4}
                >
                  <Flex align="center" mb={2}>
                    <Icon as={MdPublic} color="#f57d36" mr={2} boxSize={5} />
                    <Text fontWeight="600" color="#f57d36">
                      Broadcasting with Roaming Enabled
                    </Text>
                  </Flex>
                  <Text mb={3} fontSize="md">
                    This will broadcast the current location details to the OCPI
                    network, making it visible to all connected roaming
                    partners.
                  </Text>
                  <Text fontWeight="500" fontSize="sm" color="gray.600">
                    Any recent changes to this location will be synchronized
                    with the OCPI hub.
                  </Text>
                </Box>
              ) : (
                <Box
                  p={3}
                  bg="rgba(245, 125, 54, 0.08)"
                  borderRadius="12px"
                  mb={4}
                  border="1px dashed rgba(245, 125, 54, 0.3)"
                >
                  <Flex align="center" mb={2}>
                    <Icon as={MdLock} color="#f57d36" mr={2} boxSize={5} />
                    <Text fontWeight="600" color="#f57d36">
                      Warning: Broadcasting with Roaming Disabled
                    </Text>
                  </Flex>
                  <Text mb={3} fontSize="md">
                    Since roaming is currently disabled for this location,
                    broadcasting now will set all connector statuses to{" "}
                    <strong>REMOVED</strong> and effectively disable the
                    visibility of this location on roaming networks.
                  </Text>
                  <Text fontWeight="500" fontSize="sm" color="gray.600">
                    This is useful when you want to explicitly remove a location
                    from roaming partner networks.
                  </Text>
                </Box>
              )}
              <Text fontWeight="500" textAlign="center">
                Do you want to proceed with the broadcast?
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter
              pt={3}
              borderTop="1px solid rgba(245, 125, 54, 0.2)"
            >
              <Button
                ref={cancelRef}
                onClick={closeConfirmDialog}
                variant="outline"
                borderColor="rgba(245, 125, 54, 0.5)"
                color="gray.600"
                borderRadius="8px"
                transition="all 0.2s ease"
                _hover={{
                  bg: "rgba(245, 125, 54, 0.1)",
                  transform: "translateY(-1px)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={executeBroadcastHandler}
                ml={3}
                bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
                color="white"
                borderRadius="8px"
                boxShadow="0 4px 12px rgba(245, 125, 54, 0.3)"
                fontWeight="600"
                transition="all 0.2s ease"
                _hover={{
                  bgGradient: "linear(135deg, #f35626 0%, #f57d36 100%)",
                  boxShadow: "0 6px 16px rgba(245, 125, 54, 0.4)",
                  transform: "translateY(-2px)",
                }}
                _active={{
                  transform: "translateY(0)",
                  boxShadow: "0 2px 8px rgba(245, 125, 54, 0.3)",
                }}
              >
                Broadcast Now
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </GlassMorphismCard>

    {/* Per-station OCPI enablement */}
    <GlassMorphismCard mt={5}>
      <Flex mb={4} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={3}>
        <Flex alignItems="center">
          <Icon as={MdEvStation} boxSize={6} color="#f57d36" mr={2} />
          <Text fontSize="xl" fontWeight="600">
            Station-level Roaming
          </Text>
        </Flex>
        <HStack spacing={3}>
          <Select
            size="sm"
            value={stationOcpiFilter}
            onChange={(e) => setStationOcpiFilter(e.target.value)}
            borderRadius="md"
            w="170px"
          >
            <option value="all">All OCPI statuses</option>
            <option value="enabled">OCPI Enabled</option>
            <option value="disabled">OCPI Disabled</option>
          </Select>
          {stationsLoading && <Spinner size="sm" color="#f57d36" />}
        </HStack>
      </Flex>

      <Text fontSize="sm" color="gray.600" mb={4}>
        In addition to the location-level roaming setting above, each charging
        station under this location can be independently enabled or disabled
        for OCPI. Data for a station is only pulled/pushed to OCPI partners
        when both the location and that station are enabled.
      </Text>

      {!locationData?.IsOcpiEnabled && (
        <Box
          mb={4}
          p={3}
          bg="rgba(245, 125, 54, 0.08)"
          borderRadius="8px"
          border="1px dashed rgba(245, 125, 54, 0.3)"
        >
          <Text fontSize="sm" color="#f57d36" fontWeight="500">
            Roaming is disabled for this location. Enable it above before you
            can enable roaming for individual stations.
          </Text>
        </Box>
      )}

      {filteredStations.length === 0 ? (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
          {stations.length === 0
            ? "No charging stations found for this location."
            : "No stations match the selected OCPI status."}
        </Text>
      ) : (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Station ID</Th>
                <Th>Name</Th>
                <Th textAlign="center">OCPI Enabled</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredStations.map((station) => (
                <Tr key={station.Id}>
                  <Td fontFamily="mono">{station.StationId}</Td>
                  <Td>{station.Name || "—"}</Td>
                  <Td textAlign="center">
                    <HStack justify="center" spacing={2}>
                      <Switch
                        colorScheme="orange"
                        isChecked={!!station.IsOcpiEnabled}
                        isDisabled={
                          updatingStationId === station.Id ||
                          !locationData?.IsOcpiEnabled
                        }
                        onChange={(e) =>
                          handleStationToggle(station, e.target.checked)
                        }
                      />
                      {updatingStationId === station.Id ? (
                        <Spinner size="xs" color="#f57d36" />
                      ) : (
                        <Badge
                          colorScheme={station.IsOcpiEnabled ? "green" : "gray"}
                          borderRadius="full"
                        >
                          {station.IsOcpiEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Station disable confirmation dialog */}
      <AlertDialog
        isOpen={stationConfirm.open}
        leastDestructiveRef={stationCancelRef}
        onClose={closeStationConfirm}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay bg="rgba(0, 0, 0, 0.2)" backdropFilter="blur(12px)">
          <AlertDialogContent
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="16px"
            border="1px solid rgba(245, 125, 54, 0.4)"
            boxShadow="0 8px 32px rgba(245, 125, 54, 0.15)"
            p={3}
          >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="600"
              color="#333"
              pb={2}
              borderBottom="1px solid rgba(245, 125, 54, 0.2)"
            >
              <Flex align="center">
                <Icon as={MdLock} mr={2} color="#f57d36" boxSize={5} />
                Disable Station Roaming
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3} fontSize="md">
                Disabling OCPI for station{" "}
                <strong>
                  {stationConfirm.station?.StationId ||
                    stationConfirm.station?.Id}
                </strong>{" "}
                will notify OCPI partners that all of its EVSEs are{" "}
                <strong>REMOVED</strong>. The station itself and its data
                remain unaffected internally.
              </Text>
              <Text fontWeight="500" textAlign="center">
                Do you want to proceed?
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter pt={3} borderTop="1px solid rgba(245, 125, 54, 0.2)">
              <Button
                ref={stationCancelRef}
                onClick={closeStationConfirm}
                variant="outline"
                borderColor="rgba(245, 125, 54, 0.5)"
                color="gray.600"
                borderRadius="8px"
              >
                Cancel
              </Button>
              <Button
                onClick={executeStationConfirmHandler}
                ml={3}
                bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
                color="white"
                borderRadius="8px"
                fontWeight="600"
              >
                Disable
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </GlassMorphismCard>
    </>
  );
};

export default Roaming;
